import React, { useCallback, useMemo } from 'react';
import { Text, View, TouchableOpacity } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import {
  DocketDisplayOtherItem,
  DocketDetailsProps,
} from '../../../common/types';
import DocketItemStyles from '../DocketItem.styles';
import { useSession } from '../../../hooks/useSession';
import { PrinterProfile } from '@oolio-group/domain';
import { bulkSelectVar, selectedDocketVar } from '../../../state/apolloVars';
import Icon from '../../Icon/Icon';
import { useColors } from '../../../hooks/useColors';

export default function OtherItemsDisplay({
  docketItem,
  onToggleDocketDetails,
}: {
  docketItem: DocketDisplayOtherItem;
  onToggleDocketDetails: (props: DocketDetailsProps) => void;
}) {
  const { translate } = useTranslation();
  const styles = DocketItemStyles();
  const notifierItems = docketItem.notifierItems;
  const { session } = useSession();
  const isBulkMode = bulkSelectVar();
  const isSelected = Object.keys(selectedDocketVar())?.length > 0;
  const { colors } = useColors();

  const assignedProfiles = useMemo(
    () =>
      session?.kitchenDisplay?.printerProfiles?.map((x: PrinterProfile) => ({
        id: x.id,
        displayStyle: x.notifiers.displayStyle,
      })) || [],
    [session?.kitchenDisplay?.printerProfiles],
  );

  const printerProfile = useMemo(
    () => assignedProfiles.find(p => p.id === docketItem.printerProfileId),
    [assignedProfiles, docketItem.printerProfileId],
  );

  // Create the callback at the top level, before any conditional returns
  const handlePress = useCallback(() => {
    // Only execute the logic if otherItems exists
    onToggleDocketDetails({
      docketId: docketItem.docketId,
      items: docketItem.items,
      notifierItems: notifierItems,
      orderNumber: docketItem.orderNumber || '',
      status: docketItem.status,
      orderTypeId: docketItem.orderTypeId,
      displayStyle: printerProfile?.displayStyle,
    });
  }, [
    docketItem,
    notifierItems,
    onToggleDocketDetails,
    printerProfile?.displayStyle,
  ]);

  // Add safety checks
  if (
    !notifierItems ||
    !notifierItems.length ||
    !notifierItems[0]?.items?.length
  ) {
    return null;
  }

  const totalItemQuantity = notifierItems.reduce((total, item) => {
    if (item.items) {
      return (
        total +
        item.items.reduce((itemTotal, subItem) => {
          return itemTotal + (subItem.quantity || 0);
        }, 0)
      );
    }
    return 0;
  }, 0);

  return (
    <TouchableOpacity onPress={handlePress} disabled={isBulkMode || isSelected}>
      <View style={styles.otherItemsContainer}>
        <Text style={styles.otherItemsText}>
          {totalItemQuantity > 1
            ? `${translate('kitchenDisplay.otherItems')} (${totalItemQuantity})`
            : `${translate('kitchenDisplay.otherItem')} (${totalItemQuantity})`}
        </Text>
        <Icon color={colors.text} name={'angle-right'} />
      </View>
    </TouchableOpacity>
  );
}
